import { Colors } from '@oy/uicore';
import { IconName, IconProps, SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React, { HTMLProps } from 'react';
import { Button, ButtonProps } from '../Button';
import styles from './Message.module.css';

export enum MessageType {
  Alert = 'alert',
  Error = 'error',
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
  ExclamationError = 'exclamationError',
}

export const iconProperty: Record<MessageType, IconProps> = {
  error: {
    color: Colors.RED100,
    name: 'close-filled',
  },
  success: {
    color: Colors.GREEN100,
    name: 'checkmark-selected',
  },
  warning: {
    color: Colors.YELLOW100,
    name: 'warning',
  },
  info: {
    color: Colors.BLUE100,
    name: 'information-filled',
  },
  alert: {
    color: Colors.ORANGE100,
    name: 'warning',
  },
  exclamationError: {
    color: Colors.RED100,
    name: 'exclamation',
  },
};

export type MessageProps = {
  type?: MessageType;
  showIcon?: boolean;
  iconPosition?: 'left' | 'right';
  btnPrimaryText?: string;
  btnSecondaryText?: string;
  btnPrimaryOnClick?: () => void;
  btnSecondaryOnClick?: () => void;
  btnPrimaryProps?: Partial<ButtonProps>;
  btnSecondaryProps?: Partial<ButtonProps>;
  customIcon?: IconName;
} & HTMLProps<HTMLDivElement>;

function Message({
  type = MessageType.Info,
  children,
  className,
  showIcon = false,
  iconPosition = 'left',
  btnPrimaryText,
  btnPrimaryOnClick,
  btnSecondaryText,
  btnSecondaryOnClick,
  btnPrimaryProps = {},
  btnSecondaryProps = {},
  customIcon,
  ...props
}: MessageProps) {
  return (
    <div
      {...props}
      className={classNames(
        className,
        styles.container,
        styles[type],
        'font-body-MdRegular',
      )}
    >
      <div className={styles.contentContainer}>
        {showIcon && iconPosition === 'left' && (
          <SystemIcon
            className={styles.icon}
            {...iconProperty[type]}
            name={customIcon ? customIcon : iconProperty[type].name}
            size={24}
          />
        )}
        <div className={styles.content}>{children}</div>
        {showIcon && iconPosition === 'right' && (
          <SystemIcon
            className={styles.icon}
            {...iconProperty[type]}
            name={customIcon ? customIcon : iconProperty[type].name}
            size={24}
          />
        )}
      </div>
      <div className={classNames({ [styles.actionButton]: showIcon })}>
        {(btnPrimaryText || btnPrimaryOnClick) && (
          <Button
            size={Button.Size.Small}
            onClick={btnPrimaryOnClick}
            {...(btnPrimaryProps as any)}
            buttonType={btnPrimaryProps.buttonType || Button.Type.Text}
          >
            {btnPrimaryText}
          </Button>
        )}
        {(btnSecondaryText || btnSecondaryOnClick) && (
          <Button
            size={Button.Size.Small}
            onClick={btnSecondaryOnClick}
            {...(btnSecondaryProps as any)}
            buttonType={btnSecondaryProps.buttonType || Button.Type.Text}
          >
            {btnSecondaryText}
          </Button>
        )}
      </div>
    </div>
  );
}

export default Object.assign(Message, {
  Type: MessageType,
});
